import clsx from 'clsx';
import { ReactComponent as ArrowDownIcon } from 'intakeOptimization/assets/svg/arrow-down.svg';
import { FC, useEffect, useRef, useState } from 'react';

import { Button, InputOption, MobileDropdown, Radio } from 'lifestance-ui';

import { enableBodyScroll, onClickOut, stateAbbrToName, useDevice } from 'intakeOptimization/utils';
import { simpleKeyboardControl } from 'intakeOptimization/utils/keyboardAccessibility';
import { useLastEventClickOrTab } from 'intakeOptimization/utils/lastEventClickOrTab';

import styles from './DistanceFilter.module.scss';

interface DistanceFilterProps {
  onChange: (value: string) => void;
  entireState: string;
  state: string;
  zipCode: string | number;
  large?: boolean;
  defaultValue?: number;
  isSecondInstance?: boolean;
}

const distanceOptions = [
  {
    label: 'Within 5 miles',
    value: '5',
  },
  {
    label: 'Within 10 miles',
    value: '10',
  },
  {
    label: 'Within 25 miles',
    value: '25',
  },
  {
    label: 'Within 60 miles',
    value: '60',
  },
  {
    label: 'Entire state',
    value: 'entire_state',
  },
];

export const DistanceFilterAdmin: FC<DistanceFilterProps> = ({
  onChange,
  entireState,
  state,
  zipCode,
  large = false,
  defaultValue,
  isSecondInstance = false,
}) => {
  const [open, setOpen] = useState<boolean | null>(false);
  const [selected, setSelected] = useState<string | number | null>(
    distanceOptions[3]?.value as string,
  );
  const [selectedLabel, setSelectedLabel] = useState<number | string | undefined>(defaultValue);
  const [closedByOut, setClosedByOut] = useState<boolean>(false);
  const { isMobile, isTablet, isSmallTablet } = useDevice();
  const { lastEventType } = useLastEventClickOrTab();

  const triggerRef = useRef(null);
  const containerRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (entireState === 'true') {
      setSelected('entire_state');
    } else {
      setSelected(
        distanceOptions.find((o) => o.value?.toString() === selected?.toString())?.value as string,
      );
    }
  }, [entireState]);

  const handleSaveAndClose = () => {
    if (selected !== 'entire_state') {
      setSelectedLabel(selected as string);
    }
    if (lastEventType === 'tab') {
      containerRef.current?.focus();
    }
    setClosedByOut(false);
    onChange(selected as string);
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      setClosedByOut(false);
      setTimeout(() => {
        enableBodyScroll();
      }, 500);
    }
    if (open && !isMobile) {
      setClosedByOut(true);
      return onClickOut({
        triggerRef,
        action: () => {
          setOpen(false);
        },
        ignoreParent: true,
      });
    }
  }, [open, onChange, triggerRef]);

  useEffect(() => {
    if (!open && closedByOut) {
      handleSaveAndClose();
    }
  }, [open]);

  const handleChange = (value: string) => {
    setSelected(value);
  };

  const handleChangeMobile = (value: string) => {
    setSelectedLabel(value as string);
    onChange(selected as string);
    setSelected(value);
    onChange(value);
    setOpen(false);
    setTimeout(() => {
      enableBodyScroll();
    }, 500);
  };

  const handleOpen = () => {
    if (!open && !closedByOut) {
      setOpen(true);
    }
  };

  const initialValue = entireState === 'true'
    ? 'entire_state'
    : distanceOptions.find((o) => o.value?.toString() === selected?.toString())?.value;

  return (
    <span className={clsx(styles.container, { [styles.large]: large })}>
      <span
        style={{
          marginTop: isMobile ? '4px' : '0px',
        }}
      >
        <span
          ref={containerRef}
          className={clsx(styles.distanceFilter, {
            [styles.arrowButton]: !open,
            [styles.arrowButtonUp]: open,
          })}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => simpleKeyboardControl(e, handleOpen, true)}
          onClick={handleOpen}
        >
          {entireState === 'true' ? (
            <>
              <span className={styles.text}>{stateAbbrToName(state)}</span>
              <ArrowDownIcon />
            </>
          ) : (
            <>
              <span className={styles.text}>
                {selectedLabel}
                &nbsp;miles from&nbsp;
                {zipCode}
              </span>
              <ArrowDownIcon />
            </>
          )}
        </span>
        {!isMobile && open && (
          <div ref={triggerRef} className={styles.optionsContainer}>
            <div className={styles.content}>
              <Radio
                optionClassName={styles.option}
                name="appointment"
                defaultValue={initialValue as string}
                options={distanceOptions as InputOption[]}
                onChange={(value) => handleChange(value)}
                className={styles.clean}
                autoFocus
                ariaLabel="Select a distance"
              />
            </div>
            <div className={styles.footer}>
              <Button size="small" onClick={handleSaveAndClose} testId="SaveButton">
                Save
              </Button>
            </div>
          </div>
        )}
      </span>
      {(isMobile || isSmallTablet || isTablet) && open && (
        <div className={styles.mobilefilter}>
          <MobileDropdown
            options={distanceOptions}
            open={open}
            handleSelect={handleChangeMobile}
            mobileClose={() => setOpen(false)}
            value={defaultValue}
            icon="CloseIcon"
            title="Distance"
            sideWays={false}
            testId="DistanceDropdown"
            variant="radio"
            avoidOpeniOsStyle
          />
        </div>
      )}
    </span>
  );
};
